import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PartyPopper, Radio, Users, Camera, Flame, Zap, AlertTriangle, Rocket,
  Plus, MessageCircle, Download, ClipboardList, Flag, CheckCircle2, Radio as Wifi,
  TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { useAuth } from "../../context/AuthContext";
import { getPromoterDashboard } from "../../services/eventService";
import { getMyRevenueByMonth } from "../../services/walletService";
import { getMyDisputes } from "../../services/disputeService";
import { downloadMediaFile } from "../../services/uploadService";
import Loader from "../../components/common/Loader";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Badge from "../../components/common/Badge";
import EmptyState from "../../components/common/EmptyState";
import EventLimitModal from "../../components/common/EventLimitModal";
import DraftPromptModal from "../../components/common/DraftPromptModal";
import { useCreateEventGate } from "../../hooks/useCreateEventGate";
import { toast } from "react-toastify";
import { color, radius } from "../../styles/theme";

const STATUS_VARIANT = { Upcoming: "primary", Live: "success", Closed: "neutral" };
const REVENUE_MONTHS_BACK = 6;
const DISPUTES_PAGE_SIZE = 200;
const MONTH_FORMAT = { month: "short", year: "2-digit" };

const formatRevenueBuckets = (buckets) =>
  (buckets || []).map((bucket) => ({
    label: new Date(bucket.Year, bucket.Month - 1, 1).toLocaleDateString(undefined, MONTH_FORMAT),
    total: Number(bucket.Total) || 0,
  }));

const tallyDisputesByStatus = (disputes) => {
  const counts = {};
  (disputes || []).forEach((dispute) => {
    const key = dispute.Status || "Unknown";
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);
};

const Dashboard = () => {
  const { t } = useTranslation(["dashboard", "analytics", "common"]);
  const { user, isSubAccount, subAccountPermissions } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [revenueBuckets, setRevenueBuckets] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const canViewRevenue = !isSubAccount || Boolean(subAccountPermissions?.CanManageWallet);
  const canViewDisputes = !isSubAccount || Boolean(subAccountPermissions?.CanManageSupport);
  const {
    checking, openCreateEvent, limitInfo, closeLimitModal, sendLimitRequest, sendingRequest,
    draftPrompt, resumeDraft, discardDraftAndCreate, cancelDraftPrompt,
  } = useCreateEventGate();

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const requests = [
      getPromoterDashboard({ signal: controller.signal }),
      canViewRevenue ? getMyRevenueByMonth(REVENUE_MONTHS_BACK) : Promise.resolve(null),
      canViewDisputes ? getMyDisputes({ pageNumber: 1, pageSize: DISPUTES_PAGE_SIZE }) : Promise.resolve(null),
    ];

    Promise.allSettled(requests)
      .then(([dashboardResult, revenueResult, disputesResult]) => {
        if (!active) return;

        if (dashboardResult.status === "rejected") {
          toast.error(t("toast.loadFailed"));
          return;
        }

        setData(dashboardResult.value.data?.Result || dashboardResult.value.data);
        if (revenueResult.status === "fulfilled" && revenueResult.value) {
          setRevenueBuckets(formatRevenueBuckets(revenueResult.value.data?.Result));
        }
        if (disputesResult.status === "fulfilled" && disputesResult.value) {
          setDisputes(disputesResult.value.data?.Result || []);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [canViewRevenue, canViewDisputes, t]);

  const disputeCounts = useMemo(() => tallyDisputesByStatus(disputes), [disputes]);
  const maxDisputeCount = Math.max(1, ...disputeCounts.map((item) => item.count));

  if (loading) return <Loader />;
  if (!data) return <p style={{ color: color.textMuted, padding: 20 }}>{t("loadError")}</p>;

  const totalEvents = data.TotalEvents ?? 0;
  const stats = [
    { label: t("stats.totalEvents"), value: data.TotalEvents ?? 0, icon: PartyPopper, tone: "primary" },
    { label: t("stats.activeEvents"), value: data.ActiveEvents ?? 0, icon: Radio, tone: "success" },
    { label: t("stats.totalParticipants"), value: data.TotalParticipants ?? 0, icon: Users, tone: "warning" },
    { label: t("stats.totalMedia"), value: data.TotalMedia ?? 0, icon: Camera, tone: "danger" },
    { label: t("analytics:avgParticipants"), value: totalEvents ? Math.round((data.TotalParticipants ?? 0) / totalEvents) : 0, icon: Users, tone: "warning" },
    { label: t("analytics:avgMedia"), value: totalEvents ? Math.round((data.TotalMedia ?? 0) / totalEvents) : 0, icon: Camera, tone: "danger" },
  ];
  const hasRevenue = revenueBuckets.some((bucket) => bucket.total > 0);

  const recentEvents = data.RecentEvents || [];
  const recentMedia = data.RecentMedia || [];
  const recentActivities = data.RecentActivities || [];

  const getDownloadUrl = (media) => {
    if (!media) return null;
    if (media.VideoURL) return media.VideoURL;
    return media.Url || null;
  };

  const getDownloadName = (media) => {
    const source = getDownloadUrl(media) || "";
    const sourceName = source.split("?")[0].split("/").pop();
    if (sourceName) return sourceName;
    const ext = media?.VideoURL ? "mp4" : "jpg";
    return `media-${media?.Id || "file"}.${ext}`;
  };

  const downloadMedia = async (media) => {
    const url = getDownloadUrl(media);
    if (!url) {
      toast.error(t("toast.downloadUrlMissing"));
      return;
    }
    try {
      await downloadMediaFile({ url, fileName: getDownloadName(media) });
    } catch {
      toast.error(t("toast.downloadFailed"));
    }
  };

  return (
    <div>
      <h2 style={s.heading}>{t("welcome", { name: user?.Name || user?.UserName || user?.Email || t("welcomeDefaultName") })}</h2>
      <p style={s.sub}>{t("subtitle")}</p>

      <div style={s.statsGrid}>
        {stats.map((st) => (
          <Card key={st.label} style={s.statCard}>
            <span style={{ ...s.statIcon, background: TINT[st.tone].bg, color: TINT[st.tone].fg }}>
              <st.icon size={20} strokeWidth={2} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...s.statValue, color: TINT[st.tone].fg }}>{st.value}</div>
              <div style={s.statLabel}>{st.label}</div>
            </div>
          </Card>
        ))}
      </div>

      {canViewRevenue && (
        <Card style={s.section}>
          <h3 style={{ ...s.sectionTitle, marginBottom: 16 }}><TrendingUp size={16} strokeWidth={2.25} color={color.gray500} /> {t("analytics:revenueTrend.title")}</h3>
          {hasRevenue ? (
            <div style={s.chartWrap}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={revenueBuckets} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={color.gray100} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: color.textMuted }} axisLine={{ stroke: color.gray200 }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: color.textMuted }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip
                    formatter={(value) => [Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), t("analytics:revenueTrend.tooltipLabel")]}
                    contentStyle={{ borderRadius: radius.sm, border: `1px solid ${color.border}`, fontSize: 12 }}
                  />
                  <Bar dataKey="total" fill={color.primary500} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState icon={TrendingUp} text={t("analytics:revenueTrend.empty")} />
          )}
        </Card>
      )}

      {canViewDisputes && (
        <Card style={s.section}>
          <h3 style={{ ...s.sectionTitle, marginBottom: 16 }}><AlertTriangle size={16} strokeWidth={2.25} color={color.gray500} /> {t("analytics:disputeBreakdown.title")}</h3>
          {disputeCounts.length === 0 ? (
            <EmptyState icon={AlertTriangle} text={t("analytics:disputeBreakdown.empty")} />
          ) : (
            <div style={s.disputeList}>
              {disputeCounts.map((item) => (
                <div key={item.status} style={s.disputeRow}>
                  <span style={s.disputeStatus}>{item.status}</span>
                  <div style={s.disputeBarTrack}>
                    <div style={{ ...s.disputeBarFill, width: `${(item.count / maxDisputeCount) * 100}%` }} />
                  </div>
                  <span style={s.disputeCount}>{item.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card style={s.section}>
        <div style={s.sectionHeader}>
          <h3 style={s.sectionTitle}><Flame size={16} strokeWidth={2.25} color={color.gray500} /> {t("eventPerformance.title")}</h3>
          {recentEvents.length > 0 && <span style={s.sectionSub}>{t("eventPerformance.lastEvents", { count: recentEvents.length })}</span>}
        </div>
        {recentEvents.length === 0 ? (
          <EmptyState icon={PartyPopper} text={t("eventPerformance.empty")} />
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{t("eventPerformance.table.eventName")}</th>
                  <th style={s.th}>{t("eventPerformance.table.participants")}</th>
                  <th style={s.th}>{t("eventPerformance.table.media")}</th>
                  <th style={s.th}>{t("eventPerformance.table.status")}</th>
                  <th style={s.th}>{t("eventPerformance.table.created")}</th>
                </tr>
              </thead>
              <tbody>
                {recentEvents.map((evt) => (
                  <tr key={evt.EventId} className="table-row-hover" style={s.tr} onClick={() => evt.EventId && navigate(`/events/${evt.EventId}`)}>
                    <td style={s.td}><span style={s.evtName}>{evt.Name}</span></td>
                    <td style={s.td}>{evt.Participants ?? 0}</td>
                    <td style={s.td}>{evt.MediaCount ?? 0}</td>
                    <td style={s.td}>{evt.Status ? <Badge variant={STATUS_VARIANT[evt.Status] || "neutral"}>{evt.Status}</Badge> : <Badge variant="neutral">—</Badge>}</td>
                    <td style={s.td}>{evt.CreatedAt ? new Date(evt.CreatedAt).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={s.bottomRow}>
        <Card style={{ ...s.section, flex: 1, minWidth: 280 }}>
          <h3 style={s.sectionTitle}><Zap size={16} strokeWidth={2.25} color={color.gray500} /> {t("liveActivity.title")}</h3>
          <div style={s.activityList}>
            {recentActivities.length > 0 ? (
              recentActivities.slice(0, 8).map((act, i) => (
                <div key={i} style={s.activityItem}>
                  <span style={s.activityDot} />
                  <span style={s.activityText}>{act.Message || act.Description || JSON.stringify(act)}</span>
                </div>
              ))
            ) : (
              <EmptyState text={t("liveActivity.empty")} />
            )}
            <p style={s.hint}><Wifi size={11} strokeWidth={2} /> {t("liveActivity.hint")}</p>
          </div>
        </Card>

        <Card style={{ ...s.section, flex: 1, minWidth: 280 }}>
          <h3 style={s.sectionTitle}><Camera size={16} strokeWidth={2.25} color={color.gray500} /> {t("recentMedia.title")}</h3>
          <div style={s.mediaList}>
            {recentMedia.length > 0 ? (
              recentMedia.slice(0, 5).map((m, i) => (
                <div key={i} style={s.mediaItem}>
                  <img src={m.Url} alt="media" style={s.mediaThumbnail} onError={(e) => { e.target.style.display = "none"; }} />
                  <div style={s.mediaInfo}>
                    <span style={s.mediaEvent}>{m.EventName || "—"}</span>
                    <span style={s.mediaBy}>{t("recentMedia.uploadedBy", { name: m.UploadedBy || t("recentMedia.unknownUploader") })}</span>
                  </div>
                  <button className="btn btn-secondary btn-sm" style={s.mediaDownloadBtn} onClick={() => downloadMedia(m)} title={t("recentMedia.downloadTitle")}>
                    <Download size={13} strokeWidth={2.25} />
                  </button>
                  <span style={s.mediaDate}>{m.CreatedAt ? new Date(m.CreatedAt).toLocaleDateString() : ""}</span>
                </div>
              ))
            ) : (
              <EmptyState text={t("recentMedia.empty")} />
            )}
          </div>
        </Card>
      </div>

      <Card style={s.section}>
        <h3 style={s.sectionTitle}><AlertTriangle size={16} strokeWidth={2.25} color={color.gray500} /> {t("actionRequired.title")}</h3>
        <div style={s.activityList}>
          <div style={s.placeholderItem}><ClipboardList size={15} strokeWidth={2} color={color.gray500} /><span style={s.placeholderText}>{t("actionRequired.pendingApprovals")}</span><span style={s.placeholderBadge}>—</span></div>
          <div style={s.placeholderItem}><Flag size={15} strokeWidth={2} color={color.gray500} /><span style={s.placeholderText}>{t("actionRequired.flaggedContent")}</span><span style={s.placeholderBadge}>—</span></div>
          <div style={s.placeholderItem}><CheckCircle2 size={15} strokeWidth={2} color={color.gray500} /><span style={s.placeholderText}>{t("actionRequired.verificationRequests")}</span><span style={s.placeholderBadge}>—</span></div>
          <p style={s.hint}>{t("actionRequired.hint")}</p>
        </div>
      </Card>

      <Card style={s.section}>
        <h3 style={s.sectionTitle}><Rocket size={16} strokeWidth={2.25} color={color.gray500} /> {t("quickActions.title")}</h3>
        <div style={s.quickActions}>
          <Button icon={Plus} onClick={() => openCreateEvent("Public")} disabled={checking}>{checking ? t("quickActions.checking") : t("quickActions.createEvent")}</Button>
          <Button variant="secondary" icon={PartyPopper} onClick={() => navigate("/events")}>{t("quickActions.viewAllEvents")}</Button>
          <Button variant="secondary" icon={MessageCircle} onClick={() => navigate("/rooms")}>{t("quickActions.openChatRooms")}</Button>
        </div>
      </Card>

      {limitInfo && (
        <EventLimitModal message={limitInfo.message} sending={sendingRequest} onClose={closeLimitModal} onSendRequest={sendLimitRequest} />
      )}
      {draftPrompt && (
        <DraftPromptModal onClose={cancelDraftPrompt} onResume={resumeDraft} onDiscard={discardDraftAndCreate} />
      )}
    </div>
  );
};

const TINT = {
  primary: { bg: color.primary50, fg: color.primary600 },
  success: { bg: color.success50, fg: color.success500 },
  warning: { bg: color.warning50, fg: color.warning500 },
  danger: { bg: color.danger50, fg: color.danger500 },
};

const s = {
  heading: { margin: "0 0 4px", fontSize: 22, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  sub: { margin: "0 0 26px", color: color.textMuted, fontSize: 13.5 },

  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 16, marginBottom: 28 },
  statCard: { display: "flex", alignItems: "center", gap: 14 },
  statIcon: { width: 44, height: 44, borderRadius: radius.sm, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: 23, fontWeight: 800, lineHeight: 1.1 },
  // No whiteSpace:nowrap — a longer label ("Avg. Participants/Event") was
  // forced onto one line with nothing to wrap or truncate it, so it
  // overflowed past the card's edge instead of shrinking to fit. Wrapping
  // is the more readable fix over ellipsis-truncating a short label like
  // this one; the text column above also needs minWidth:0 for a flex child
  // to actually be allowed to wrap instead of stretching the card.
  statLabel: { fontSize: 11, color: color.textMuted, marginTop: 3 },

  section: { marginBottom: 24 },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  sectionTitle: { margin: "0 0 0", fontSize: 15, fontWeight: 700, color: color.textPrimary, display: "flex", alignItems: "center", gap: 8 },
  sectionSub: { fontSize: 12, color: color.textMuted, background: color.gray100, padding: "3px 10px", borderRadius: radius.pill },

  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700,
    color: color.textMuted, textTransform: "uppercase", letterSpacing: 0.5,
    borderBottom: `2px solid ${color.gray100}`, whiteSpace: "nowrap",
  },
  tr: { cursor: "pointer" },
  td: { padding: "12px 14px", borderBottom: `1px solid ${color.gray100}`, color: color.gray700, whiteSpace: "nowrap" },
  evtName: { fontWeight: 600, color: color.textPrimary },

  bottomRow: { display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 0 },

  activityList: { display: "flex", flexDirection: "column", gap: 10, marginTop: 4 },
  activityItem: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: color.gray700 },
  activityDot: { width: 7, height: 7, borderRadius: "50%", background: color.success500, flexShrink: 0 },
  activityText: { fontSize: 13 },
  hint: { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: color.textMuted, margin: "10px 0 0" },

  placeholderItem: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: color.gray700, padding: "9px 0", borderBottom: `1px solid ${color.gray100}` },
  placeholderText: { flex: 1 },
  placeholderBadge: { fontSize: 12, color: color.textMuted, fontWeight: 600 },

  quickActions: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 2 },

  chartWrap: { marginTop: 4 },
  disputeList: { display: "flex", flexDirection: "column", gap: 12 },
  disputeRow: { display: "flex", alignItems: "center", gap: 12 },
  disputeStatus: { fontSize: 12.5, fontWeight: 600, color: color.textPrimary, width: 140, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  disputeBarTrack: { flex: 1, height: 10, background: color.gray100, borderRadius: radius.pill, overflow: "hidden" },
  disputeBarFill: { height: "100%", background: color.primary500, borderRadius: radius.pill },
  disputeCount: { fontSize: 12.5, fontWeight: 700, color: color.textMuted, width: 28, textAlign: "right", flexShrink: 0 },

  mediaList: { display: "flex", flexDirection: "column", gap: 10, marginTop: 4 },
  mediaItem: { display: "flex", alignItems: "center", gap: 12, fontSize: 13, color: color.gray700, padding: "8px 0", borderBottom: `1px solid ${color.gray100}` },
  mediaThumbnail: { width: 44, height: 44, borderRadius: radius.sm, objectFit: "cover", flexShrink: 0, background: color.gray100 },
  mediaInfo: { flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
  mediaEvent: { fontWeight: 600, color: color.textPrimary, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  mediaBy: { fontSize: 11, color: color.textMuted },
  mediaDownloadBtn: { width: 28, height: 28, padding: 0, flexShrink: 0 },
  mediaDate: { fontSize: 11, color: color.textMuted, flexShrink: 0, whiteSpace: "nowrap" },
};

export default Dashboard;
